import { useCallback, useEffect, useRef, useState } from "react";
import type {
	UsageLogRowDto,
	UsageLogStatus,
	UsageLogSummaryDto,
	UsageLogVendor,
} from "@/types/ai-assistant-usage-log";
import { fetchUsageLog, fetchUsageLogSummary } from "./usage-log.api";

/** Pilihan rentang di filter tabel log (hari kalender WITA). */
export const USAGE_LOG_RANGE_OPTIONS = [1, 7, 30] as const;
const DEFAULT_RANGE_DAYS = 7;

export interface UsageLogFilters {
	days: number;
	vendor?: UsageLogVendor;
	status?: UsageLogStatus;
}

export interface AssistantUsageLogState {
	/** null = belum pernah selesai dimuat (memuat pertama atau gagal). */
	rows: UsageLogRowDto[] | null;
	summary: UsageLogSummaryDto | null;
	nextCursor: string | null;
	loading: boolean;
	loadingMore: boolean;
	error: string | null;
	filters: UsageLogFilters;
	setFilters: (next: UsageLogFilters) => void;
	loadMore: () => void;
}

const DEFAULT_FILTERS: UsageLogFilters = { days: DEFAULT_RANGE_DAYS };

/** Muat halaman pertama log + ringkasan tiap kali filter atau `reloadKey` berubah. */
export function useAssistantUsageLog(
	reloadKey: unknown,
): AssistantUsageLogState {
	const [filters, setFilters] = useState<UsageLogFilters>(DEFAULT_FILTERS);
	const [rows, setRows] = useState<UsageLogRowDto[] | null>(null);
	const [summary, setSummary] = useState<UsageLogSummaryDto | null>(null);
	const [nextCursor, setNextCursor] = useState<string | null>(null);
	const [loading, setLoading] = useState(true);
	const [loadingMore, setLoadingMore] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const abortRef = useRef<AbortController | null>(null);

	const load = useCallback(async (active: UsageLogFilters) => {
		abortRef.current?.abort();
		const controller = new AbortController();
		abortRef.current = controller;
		setLoading(true);
		setError(null);
		try {
			const [page, totals] = await Promise.all([
				fetchUsageLog(active, controller.signal),
				fetchUsageLogSummary(active.days, controller.signal),
			]);
			setRows(page.rows);
			setNextCursor(page.nextCursor);
			setSummary(totals);
		} catch (err) {
			if (err instanceof Error && err.name === "AbortError") return;
			setRows(null);
			setError(
				err instanceof Error ? err.message : "Gagal memuat log penggunaan",
			);
		} finally {
			if (!controller.signal.aborted) setLoading(false);
		}
	}, []);

	// biome-ignore lint/correctness/useExhaustiveDependencies: reloadKey sengaja memicu muat ulang
	useEffect(() => {
		load(filters);
		return () => abortRef.current?.abort();
	}, [load, filters, reloadKey]);

	const loadMore = useCallback(async () => {
		if (!nextCursor) return;
		setLoadingMore(true);
		try {
			const page = await fetchUsageLog({ ...filters, cursor: nextCursor });
			setRows((prev) => [...(prev ?? []), ...page.rows]);
			setNextCursor(page.nextCursor);
		} catch (err) {
			setError(
				err instanceof Error ? err.message : "Gagal memuat log penggunaan",
			);
		} finally {
			setLoadingMore(false);
		}
	}, [filters, nextCursor]);

	return {
		rows,
		summary,
		nextCursor,
		loading,
		loadingMore,
		error,
		filters,
		setFilters,
		loadMore,
	};
}
